import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect
} from "react";
import { UserData } from "@/entities/users/types";
import { useReport } from "@/entities/reporting/hooks";
import {
  ChartDataType,
  ChartSingleValue,
  ReportType,
  SettingsPeriod,
  WinRateData,
  WinRateDirectReportsResponse,
  WinRateOverTimeFrame
} from "@/entities/reporting/types";
import { useTranslation } from "react-i18next";
import { ComparisonChartProps } from "@/components/graphs/ComparisonChart/ComparisonChart";
import WinRate from "./WinRate";
import { reducePrecision } from "@/utils/numbers";
import { getCurrentLocale } from "@/utils/locale";
import { debounce } from "lodash";
import { useDirectReportsCheck } from "@/components/shared/useDirectReportsCheck";

interface Props {
  userData?: UserData;
  isAE: boolean;
}

const WinRateWrapper: React.FC<Props> = ({ userData, isAE }) => {
  const { t } = useTranslation();

  const [reportPeriod, setReportPeriod] = useState(
    SettingsPeriod.LAST_12_MONTHS
  );

  // Keep track of the last successful data using ref to avoid re-render loops
  const lastValidReportRef = useRef<WinRateData | null>(null);

  const { report, isLoading } = useReport(ReportType.WIN_RATE, reportPeriod);

  const { hasDirectReports, directReportsData } =
    useDirectReportsCheck<WinRateDirectReportsResponse>(
      userData?.id,
      reportPeriod,
      !isAE,
      {
        reportType: ReportType.WIN_RATE,
        responseKey: "winRate",
        checkEmptyFn: (data) => {
          // Check if winRates object has any keys (direct report IDs)
          return (
            data?.overall?.winRates &&
            Object.keys(data.overall.winRates).length > 0
          );
        }
      }
    );

  const debouncedPeriodSelect = useCallback(
    debounce((period: SettingsPeriod) => {
      setReportPeriod(period);
    }, 300),
    []
  );

  const currentReport = report?.winRate ?? undefined;

  useEffect(() => {
    if (currentReport) {
      lastValidReportRef.current = currentReport;
    }
  }, [currentReport]);

  // Use current data if available, otherwise fall back to last valid data
  const winRateReport: WinRateData =
    currentReport ?? lastValidReportRef.current ?? undefined;

  const processedOverall: ComparisonChartProps = useMemo(() => {
    if (!report || !winRateReport) return { datasets: [] };
    const overallData = winRateReport.winRate.overall.winRates;
    return {
      datasets: [
        {
          name: t(`comparison.titles.you`),
          value: reducePrecision(overallData.you || 0)
        },
        {
          name: t(`comparison.titles.team`),
          value: reducePrecision(overallData.team || 0)
        },
        {
          name: t(`comparison.titles.organization`),
          value: reducePrecision(overallData.org || 0)
        },
        {
          name: t(`comparison.titles.industry`),
          value: reducePrecision(overallData.industry || 0)
        }
      ]
    };
  }, [report, winRateReport, t]);

  const processWinRateData = useCallback(
    (
      timeFrame: WinRateOverTimeFrame,
      data?: typeof winRateReport
    ): ChartDataType[] => {
      if (!data) return [];

      return Object.entries(data.winRate.overTime[timeFrame]).map(
        ([date, values]: [string, ChartSingleValue]) => ({
          date: new Date(date).toLocaleDateString(getCurrentLocale(), {
            month: "2-digit",
            year: "2-digit"
          }),
          youValue: reducePrecision(values.you || 0),
          teamValue: reducePrecision(values.team || 0),
          organizationValue: reducePrecision(values.org || 0),
          industryValue: reducePrecision(values.industry || 0)
        })
      );
    },
    [report, winRateReport]
  );

  const processToMonth = processWinRateData("toMonth", winRateReport);
  const processByMonth = processWinRateData("byMonth", winRateReport);

  return (
    <WinRate
      userData={userData}
      period={reportPeriod}
      periodSelect={debouncedPeriodSelect}
      comparisonChartProps={processedOverall}
      lineChartData={processToMonth}
      barChartData={processByMonth}
      isLoading={isLoading}
      isAE={isAE}
      drillDownHidden={!hasDirectReports}
      directReportsData={directReportsData}
    />
  );
};

export default WinRateWrapper;
